import { pickInfrastructurePlan } from "../i18n/architecturePlanSimple";
import type { Locale } from "../i18n/locale";
import { numberLocaleFor } from "../i18n/locale";
import { env } from "../config/env";
import { uiString } from "../i18n/ui";
import type { AudienceMode } from "./audienceMode";
import { DEFAULT_AUDIENCE_MODE } from "./audienceMode";
import {
  DEFAULT_DEPLOYMENT_STACK,
  type DeploymentStackConfig,
} from "./deploymentStack";
import type { AwsRegionId } from "./awsRegion";
import type { CostEstimate, OptimizationResult, Workload } from "./types";

export function buildMarkdownReport(opts: {
  locale: Locale;
  region: AwsRegionId;
  workload: Workload;
  estimate: CostEstimate;
  optimization: OptimizationResult;
  title: string;
  deploymentStack?: DeploymentStackConfig;
  audience?: AudienceMode;
  /** Блок довіри / аудиту перед юридичним футером. */
  appendixMd?: string;
}): string {
  const {
    locale,
    region,
    workload,
    estimate,
    optimization,
    title,
    deploymentStack,
    audience = DEFAULT_AUDIENCE_MODE,
    appendixMd,
  } = opts;
  const stack = deploymentStack ?? DEFAULT_DEPLOYMENT_STACK;
  const nf = numberLocaleFor(locale);
  const money = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  const workloadHeading =
    locale === "uk"
      ? "Навантаження"
      : locale === "es"
        ? "Carga de trabajo"
        : "Workload";
  const monthlyHeading =
    locale === "uk"
      ? "Місячна вартість (орієнтир)"
      : locale === "es"
        ? "Coste mensual (orientativo)"
        : "Monthly cost (indicative)";

  const lines: string[] = [
    `# ${title}`,
    "",
    `**AWS region (factor):** \`${region}\``,
    "",
    `## ${workloadHeading}`,
    "",
    "```json",
    JSON.stringify(
      {
        users_daily: workload.usersDaily,
        requests_per_user: workload.requestsPerUser,
        features: workload.enabledFeatures,
        ai_request_share: workload.aiRequestShare,
        avg_prompt_tokens: workload.avgPromptTokens,
        avg_completion_tokens: workload.avgCompletionTokens,
        storage_kb_per_user: workload.storageKbPerUser,
      },
      null,
      2,
    ),
    "```",
    "",
    `## ${monthlyHeading}`,
    "",
    "| Item | USD/mo |",
    "|------|--------|",
  ];

  for (const row of estimate.lineItems) {
    lines.push(`| ${row.label} | ${money(row.monthlyUsd)} |`);
  }
  lines.push(`| **Total** | **${money(estimate.totalMonthlyUsd)}** |`, "");

  const plan = pickInfrastructurePlan(locale, workload, stack, audience);
  lines.push("## " + uiString(locale, "plan.title"), "", "");
  for (const sec of plan) {
    lines.push(`### ${sec.title}`, "");
    for (const item of sec.items) {
      lines.push(`- ${item}`);
    }
    lines.push("");
  }

  lines.push("## " + uiString(locale, "export.assumptionsHeading"), "");
  for (const a of estimate.assumptions) {
    lines.push(`- ${a}`);
  }
  lines.push("");

  if (optimization.problems.length) {
    lines.push("## " + uiString(locale, "rec.risks"), "");
    for (const p of optimization.problems) {
      lines.push(`- **${p.title}** — ${p.detail}`);
    }
    lines.push("");
  }

  lines.push("## " + uiString(locale, "rec.suggestions"), "");
  for (const s of optimization.suggestions) {
    lines.push(`- **${s.title}** (${s.effort}) — ${s.impact}`);
  }

  if (optimization.architecture.length) {
    lines.push("", "## " + uiString(locale, "rec.arch"), "");
    for (const a of optimization.architecture) {
      lines.push(`- **${a.title}** — ${a.rationale}`);
    }
  }

  if (appendixMd?.trim()) {
    lines.push("", appendixMd.trim());
  }

  lines.push(
    "",
    "## " + uiString(locale, "export.legalHeading"),
    "",
    uiString(locale, "footer.legal"),
    "",
    "---",
    `*${title} · ${env.contactEmail}*`,
    locale === "uk"
      ? "*Орієнтовна модель — звіряй з реальними біллами.*"
      : locale === "es"
        ? "*Modelo orientativo — verifica con las facturas reales.*"
        : "*Indicative model — verify against real bills.*",
  );
  return lines.join("\n");
}
